import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";

const LINKS = [
  { label: "HOME", id: "home" },
  { label: "ABOUT FELLESVIAR", id: "about" },
  { label: "RESEARCH", id: "research" },
  { label: "FAQ", id: "faq" },
  { label: "CONTACT US", id: "contact" },
];

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("home");
  const location = useLocation();

  useEffect(() => {
    if (location.pathname !== "/") return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px" }
    );
    LINKS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [location.pathname]);

  const go = (e, id) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      window.location.href = `/#${id}`;
      return;
    }
    scrollToId(id);
  };

  return (
    <header
      data-testid="main-nav"
      className="fixed inset-x-0 top-0 z-50 border-b border-blue-100/80 bg-white/75 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 lg:px-8">
        <Link to="/" aria-label="FellesViar home">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-6 xl:flex" data-testid="nav-links">
          {LINKS.map(({ label, id }) => (
            <a
              key={id}
              href={`/#${id}`}
              onClick={(e) => go(e, id)}
              data-testid={`nav-link-${id}`}
              className={`group relative py-2 text-[11px] font-bold tracking-[0.14em] transition-colors duration-300 ${
                active === id && location.pathname === "/"
                  ? "text-fv-blue"
                  : "text-fv-navy/70 hover:text-fv-navy"
              }`}
            >
              {label}
              <span
                className={`absolute -bottom-0.5 left-0 h-[2px] rounded-full bg-fv-blue transition-[width] duration-300 ${
                  active === id && location.pathname === "/"
                    ? "w-full"
                    : "w-0 group-hover:w-full"
                }`}
              />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="/#contact"
            onClick={(e) => go(e, "contact")}
            data-testid="enquire-now-btn"
            className="group hidden items-center gap-2 rounded-full bg-fv-blue px-6 py-2.5 text-xs font-bold tracking-[0.12em] text-white shadow-lg shadow-fv-blue/30 transition-all duration-300 hover:bg-fv-navy hover:shadow-fv-navy/30 sm:flex"
          >
            ENQUIRE NOW
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen(!open)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-blue-100 text-fv-navy xl:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-blue-100 bg-white/95 backdrop-blur-xl xl:hidden"
          >
            <div className="space-y-1 px-6 py-6">
              {LINKS.map(({ label, id }) => (
                <a
                  key={id}
                  href={`/#${id}`}
                  onClick={(e) => go(e, id)}
                  data-testid={`mobile-nav-link-${id}`}
                  className="block rounded-xl px-4 py-3 text-sm font-bold tracking-[0.1em] text-fv-navy transition-colors duration-200 hover:bg-fv-ice hover:text-fv-blue"
                >
                  {label}
                </a>
              ))}
              <a
                href="/#contact"
                onClick={(e) => go(e, "contact")}
                data-testid="mobile-enquire-btn"
                className="mt-3 block rounded-full bg-fv-blue px-4 py-3.5 text-center text-sm font-bold tracking-[0.12em] text-white"
              >
                ENQUIRE NOW
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
